import store from "@/store";
import axios from "axios";

const keycloakUrl = process.env.VUE_APP_AUTH_KEYCLOAK_URL;
const realm = process.env.VUE_APP_AUTH_KEYCLOAK_REALM;
const clientId = process.env.VUE_APP_AUTH_KEYCLOAK_CLIENT_ID;
const PROVIDER = "thaid";

/**
 * ตรวจสอบว่า user ปัจจุบันเชื่อม ThaID แล้วหรือยัง
 */
const checkThaidLinkStatus = async () => {
    const token = store.getters["userAuth/getToken"];

    try {
        const response = await axios.get(
            `${keycloakUrl}/realms/${realm}/account/linked-accounts`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            }
        );

        const accounts = Array.isArray(response.data) ? response.data : [];
        const thaidAccount = accounts.find(
            (acc) => acc.providerAlias === PROVIDER
        );

        if (thaidAccount) {
            return {
                linked: thaidAccount.connected === true,
                providerUserId: thaidAccount.linkedUsername || null,
            };
        }

        return { linked: false, providerUserId: null };
    } catch (err) {
        try {
            await axios.get(
                `${keycloakUrl}/realms/${realm}/broker/${PROVIDER}/token`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return { linked: true, providerUserId: null };
        } catch {
            return { linked: false, providerUserId: null };
        }
    }
};

/**
 * สร้าง Keycloak Broker Link URL สำหรับ Account Linking
 *
 * Keycloak 16.x ไม่มี Account REST API v2 (POST /account/linked-accounts)
 * ต้องสร้าง broker/link URL เอง:
 *   GET /realms/{realm}/broker/{provider}/link
 *       ?client_id=...&redirect_uri=...&nonce=...&hash=...
 *
 * hash = Base64URL(SHA256(nonce + sessionState + clientId + provider))
 */
const generateBrokerLinkUrl = async (redirectUri) => {
    const keycloak = store.getters["userAuth/getAllKeycloak"];
    const sessionState = keycloak.sessionId || keycloak.tokenParsed?.session_state;

    if (!sessionState) {
        throw new Error("ไม่พบ Keycloak session state");
    }

    const nonce = crypto.randomUUID();

    // hash = Base64URL(SHA256(nonce + sessionState + clientId + provider))
    const data = nonce + sessionState + clientId + PROVIDER;
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(data));
    const hashArray = new Uint8Array(hashBuffer);
    const hashBase64 = btoa(String.fromCharCode(...hashArray))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");

    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        nonce,
        hash: hashBase64,
    });

    return `${keycloakUrl}/realms/${realm}/broker/${PROVIDER}/link?${params}`;
};

/**
 * เชื่อมบัญชี ThaID ด้วย Keycloak Broker Link
 *
 * Flow:
 * 1. สร้าง broker/link URL พร้อม hash
 * 2. redirect browser ไปที่ URL
 * 3. Keycloak redirect ไป ThaID → user ยืนยันตัวตน
 * 4. ThaID redirect กลับ Keycloak → สร้าง federated_identity
 * 5. Keycloak redirect กลับ App
 */
const linkThaid = async () => {
    const redirectUri = window.location.origin + "/profile";
    const brokerLinkUrl = await generateBrokerLinkUrl(redirectUri);
    window.location.href = brokerLinkUrl;
};

/**
 * ยกเลิกการเชื่อม ThaID ของ user ปัจจุบัน
 *
 * เรียกผ่าน Backend API (kcadmin proxy) เพื่อหลีก CORS issue:
 *   DELETE ${VUE_APP_BASE_API}kcadmin/users/{userId}/federated-identity/thaid
 *
 * Backend จะ proxy ไปยัง Keycloak Admin API:
 *   DELETE /admin/realms/{realm}/users/{userId}/federated-identity/thaid
 *
 * ⚠️ ไม่สามารถเรียก Keycloak โดยตรงจาก browser ได้เพราะ:
 *   - KC 16.1.1 CORS preflight ไม่อนุญาต DELETE method (bug #13999, fixed KC 20+)
 *   - Admin API ถูก block โดย CORS (admin-cli ไม่มี Web Origins)
 */
const unlinkThaid = async () => {
    const token = store.getters["userAuth/getToken"];
    const tokenParsed = store.getters["userAuth/getTokenParsed"];
    const keycloakUserId = tokenParsed.sub;
    if (!keycloakUserId) throw new Error("ไม่พบ Keycloak user ID");

    const baseApi = process.env.VUE_APP_BASE_API;
    await axios.delete(
        `${baseApi}kcadmin/users/${keycloakUserId}/federated-identity/${PROVIDER}`,
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return true;
};

export { checkThaidLinkStatus, linkThaid, unlinkThaid };
